import type { ActionContext } from "../actions/types";
import type { TRangeCell } from "../../tableUtils";
import { colParams } from "../utils";

/**
 * Predicate type for selection-level disabled checks.
 */
export type DisabledPredicate = (ctx: ActionContext) => boolean;

/**
 * Predicate type for column-level checks.
 * Used with anyColumn/allColumns combinators.
 */
export type ColumnPredicate = (ctx: ActionContext, columnId: string) => boolean;

/**
 * Predicate type for cell-level checks.
 * Used with anyCell/allCells combinators.
 */
export type CellPredicate = (ctx: ActionContext, cell: TRangeCell) => boolean;

// COMBINATORS (selection-level)

/**
 * Returns true if ANY of the predicates return true (logical OR).
 * @example disabled: any(selection.isEmpty, selection.isMultiColumn)
 */
export function any(...predicates: DisabledPredicate[]): DisabledPredicate {
  return (ctx) => predicates.some((p) => p(ctx));
}

/**
 * Returns true if ALL predicates return true (logical AND).
 * @example disabled: all(selection.isMultiColumn, selection.isMultiRow)
 */
export function all(...predicates: DisabledPredicate[]): DisabledPredicate {
  return (ctx) => predicates.every((p) => p(ctx));
}

/**
 * Returns true if NONE of the predicates return true (logical NOR).
 * With a single predicate, acts as negation.
 * @example disabled: none(anyColumn(column.isProduct)) // no product columns
 */
export function none(...predicates: DisabledPredicate[]): DisabledPredicate {
  return (ctx) => !predicates.some((p) => p(ctx));
}

// COMBINATORS (column-level -> selection-level)

/**
 * Extracts unique column IDs from the selection context.
 * Includes ctx.columnId for header menu cases (when rangeSelection is empty).
 */
function getSelectionColumnIds(ctx: ActionContext): string[] {
  const cells = ctx.rangeSelection || [];
  const columnIds = new Set(cells.map((c) => c.columnId));
  // Include ctx.columnId for header menu case (empty rangeSelection)
  if (ctx.columnId) columnIds.add(ctx.columnId);
  return [...columnIds];
}

/**
 * Returns true if ANY column in selection matches the predicate.
 * @example disabled: anyColumn(column.isAuxiliary)
 */
export function anyColumn(predicate: ColumnPredicate): DisabledPredicate {
  return (ctx) => {
    const columnIds = getSelectionColumnIds(ctx);
    return columnIds.some((colId) => predicate(ctx, colId));
  };
}

/**
 * Returns true if ALL columns in selection match the predicate.
 * Returns false if no columns selected.
 * @example disabled: allColumns(column.isProduct)
 */
export function allColumns(predicate: ColumnPredicate): DisabledPredicate {
  return (ctx) => {
    const columnIds = getSelectionColumnIds(ctx);
    if (columnIds.length === 0) return false;
    return columnIds.every((colId) => predicate(ctx, colId));
  };
}

/**
 * Returns true if ANY of the column predicates return true for a given column (logical OR).
 * @example anyColumn(orColumn(column.isCannedCurve, column.isSourcedCurve))
 */
export function orColumn(...predicates: ColumnPredicate[]): ColumnPredicate {
  return (ctx, colId) => predicates.some((p) => p(ctx, colId));
}

// COMBINATORS (cell-level -> selection-level)

/**
 * Returns true if ANY cell in selection matches the predicate.
 * @example disabled: anyCell(cell.isEmpty)
 */
export function anyCell(predicate: CellPredicate): DisabledPredicate {
  return (ctx) => {
    const cells = ctx.rangeSelection || [];
    return cells.some((c) => predicate(ctx, c));
  };
}

// SELECTION SHAPE CHECKS (these check selection structure, not column properties)

export const selection = {
  /** True if no cells are selected. */
  isEmpty: ((ctx) => (ctx.rangeSelection?.length ?? 0) === 0) as DisabledPredicate,

  /** True if exactly one cell is selected. */
  isSingleCell: ((ctx) => (ctx.rangeSelection?.length ?? 0) === 1) as DisabledPredicate,

  /** True if selection spans multiple columns. */
  isMultiColumn: ((ctx) => {
    const cells = ctx.rangeSelection || [];
    const uniqueColumnIds = new Set(cells.map((c) => c.columnId)).size;
    return uniqueColumnIds > 1;
  }) as DisabledPredicate,

  /** True if selection spans multiple rows. */
  isMultiRow: ((ctx) => {
    const cells = ctx.rangeSelection || [];
    const uniqueRowIds = new Set(cells.map((c) => c.rowId)).size;
    return uniqueRowIds > 1;
  }) as DisabledPredicate,

  /** True if entire column(s) selected (e.g., clicked column header). */
  isFullColumn: ((ctx) => ctx.isHeaderMenu) as DisabledPredicate,

  /** True if the selected column is a period column (e.g., "Month" column). */
  isPeriodColumn: ((ctx) => ctx.headerName?.toLowerCase() === "period") as DisabledPredicate,

  /**
   * True if the clicked column is the main month column (pinned first column, colId "1").
   * Unlike the shadow columns it has no gridId, so it can't be matched by the
   * colId-based column predicates — detect it via its header params instead.
   */
  isMainMonthColumn: ((ctx) => ctx.headerParams?.mainMonth === true) as DisabledPredicate,

  /** True if the current column is an auxiliary/shadow column. Works for both cell and header selections. */
  isAuxiliaryColumn: ((ctx) => {
    const col = ctx.api.getColumn(ctx.columnId);
    const params = colParams(col);
    return Boolean(params?.shadowType || (ctx.columnId && !params?.productId));
  }) as DisabledPredicate,
} as const;

// COLUMN-LEVEL PREDICATES (use with anyColumn/allColumns)

export const column = {
  /** True if the column is a product column (has a productId). */
  isProduct: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return !!params?.productId;
  }) as ColumnPredicate,

  /** True if the column is an auxiliary/shadow column. */
  isAuxiliary: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return Boolean(colId?.includes("shadow") || params?.shadowType || (colId && !params?.productId));
  }) as ColumnPredicate,

  /** True if the column is blank. */
  isBlank: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.shadowType === "blank";
  }) as ColumnPredicate,

  /** True if the column is a canned curve. */
  isCannedCurve: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.artisType === "canned";
  }) as ColumnPredicate,

  /** True if the column artisType is "eod". */
  isEod: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.artisType === "eod";
  }) as ColumnPredicate,

  /** True if the column has a formula. */
  hasFormula: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.hasFormula ?? false;
  }) as ColumnPredicate,

  /** True if user has write access to the column. */
  hasWriteAccess: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.permissions?.some((p) => p.permission === "write") ?? false;
  }) as ColumnPredicate,

  /** True if the column is sourced (exchange curve). */
  isSourcedCurve: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.artisType === "sourced";
  }) as ColumnPredicate,

  /** True if the column is a customer curve. */
  isCustomerCurve: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.artisType === "customer_curve";
  }) as ColumnPredicate,

  /** True if the column supports manual entry (has shared cells). */
  isManualEntry: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.hasSharedCell === true;
  }) as ColumnPredicate,

  /** True if the column is canned or sourced. */
  isCannedOrSourced: ((ctx, colId) => {
    const col = ctx.api.getColumn(colId);
    const params = colParams(col);
    return params?.artisType === "canned" || params?.artisType === "sourced";
  }) as ColumnPredicate,

  /** True if the column is the first column in the grid (should not be removed). */
  isDefaultAuxiliaryColumn: ((ctx, colId) => {
    const allColumns = ctx.api.getAllDisplayedColumns();
    return allColumns?.[0]?.getColId() === colId;
  }) as ColumnPredicate,
} as const;

// CELL-LEVEL PREDICATES (use with anyCell)

export const cell = {
  /** True if the cell's value is empty (null, undefined, or empty string). */
  isEmpty: ((ctx, c) => {
    const rowNode = ctx.api.getRowNode(c.rowId);
    if (!rowNode) return true;
    const cellValue = ctx.api.getCellValue({ rowNode, colKey: c.columnId });
    return cellValue === null || cellValue === undefined || cellValue === "";
  }) as CellPredicate,

  /** True if the cell is in a custom time spread row (adhoc rowType). */
  isCustomTimeSpread: ((ctx, c) => {
    const rowNode = ctx.api.getRowNode(c.rowId);
    if (!rowNode) return false;
    return rowNode.data?.rowType === "adhoc";
  }) as CellPredicate,

  /** True if the cell is a manual-entry (shared) cell for that specific row in the column. */
  isManualEntry: ((ctx, c) => {
    const col = ctx.api.getColumn(c.columnId);
    const params = colParams(col);
    const sharedCellOffsets = params?.sharedCellOffsets ?? [];
    return sharedCellOffsets.includes(c.rowIndex.toString());
  }) as CellPredicate,
} as const;

// FEATURE FLAG CHECKS (user-level)

/**
 * Creates a predicate that checks if a feature is disabled by subscription tier.
 * Type-safe: only accepts feature names from useUserSubscriptionTier's disabledFeatures.
 * @example disabled: featureDisabled("editCurve")
 */
export function featureDisabled(featureName: keyof NonNullable<ActionContext["disabledFeatures"]>): DisabledPredicate {
  return (ctx) => !!ctx.disabledFeatures?.[featureName];
}

// SPECIAL CHECKS (context-level)

export const special = {
  /**
   * True if user's org is in the exception list for live charts.
   * Orgs 10 and 12 have special access.
   */
  isOrgException: ((ctx) => {
    const org = ctx.user?.data?.organisation;
    const orgExceptions = [12, 10];
    return org !== undefined && org !== null && orgExceptions.includes(org);
  }) as DisabledPredicate,

  liveChartBlockedWithoutHistoricalData: ((ctx) => {
    if (ctx.orgNeedsHistoricalData) return false;
    return anyColumn(column.isCustomerCurve)(ctx);
  }) as DisabledPredicate,
} as const;

// RECIPES (pre-built combinations for common use cases)

export const recipes = {
  /**
   * Disabled if: no product columns in selection.
   * Used by: column, curve, header, alerts, charts, field, range, period, etc.
   */
  productOnly: none(anyColumn(column.isProduct)),

  /**
   * Disabled if: full column selected OR no product columns.
   * Used by: alerts, add-to-favourites, format, period, range
   */
  cellOnlyProduct: any(selection.isFullColumn, none(anyColumn(column.isProduct))),

  /**
   * Disabled if: no product columns AND not an auxiliary column.
   * Allows formatting operations on both product columns and auxiliary columns.
   * Used by: column, period, range formatting menus
   */
  productOrAuxiliaryColumn: ((ctx) =>
    !anyColumn(column.isProduct)(ctx) && !anyColumn(column.isAuxiliary)(ctx)) as DisabledPredicate,

  /**
   * Disabled if: full column selected OR (no product columns AND not a period column).
   * Used by: period, range formatting when cell selection is required
   */
  cellOnlyProductOrPeriod: any(
    selection.isFullColumn,
    (ctx) => !anyColumn(column.isProduct)(ctx) && !selection.isPeriodColumn(ctx),
  ),
} as const;
